// Modalul „Новый фильтр" / „Редактировать фильтр".
//
// Partea din dreapta („Что попадёт в сетку") se recalculează la fiecare tastă:
// numărul total de monede care trec și primele 14, exact ca la ScalpX.

import { el, clear, fmtVolume, fmtPct, debounce } from '../core/util.js';
import { icon } from './icons.js';
import { modal, marketToggle, tfButtons, baseTicker } from './components.js';
import { CONDITIONS, SORT_TYPES, FILTER_ICONS, apply, blankPreset } from '../core/filters.js';
import { market } from '../core/state.js';

const PREVIEW_ROWS = 14;

/**
 * @param {object|null} preset  null => filtru nou
 * @param {(preset) => void} onSave
 */
export function openFilterModal(preset, onSave) {
  const draft = preset ? structuredClone(preset) : blankPreset();
  const isNew = !preset;

  const preview = el('div', { class: 'sfm-preview' });
  const refresh = debounce(() => renderPreview(preview, draft), 120);

  const body = el('div', { class: 'sfm' }, [
    el('div', { class: 'sfm-left' }, [
      el('div', { class: 'sfm-name-row' }, [
        el('div', { class: 'sfm-name-icon' }, [icon(draft.icon, 18)]),
        el('input', {
          class: 'sfm-name-input',
          placeholder: 'Например: Волатильность',
          value: draft.name,
          oninput: (e) => { draft.name = e.target.value; },
        }),
      ]),

      section('Условия отбора', 'включите нужные и задайте диапазон',
        el('div', { class: 'sfm-conditions' },
          CONDITIONS.map((cond) => conditionRow(cond, draft, refresh)))),

      section('Как показывать', 'рынок, таймфрейм графиков, сортировка',
        el('div', { class: 'sfm-display' }, [
          field('ИКОНКА', iconPicker(draft)),
          field('РЫНОК', marketToggle(draft.market, (v) => { draft.market = v; refresh(); }, { withAll: true })),
          field('ТАЙМФРЕЙМ ГРАФИКОВ', tfButtons(draft.chartTf, (tf) => { draft.chartTf = tf; })),
          field('МАКСИМУМ МОНЕТ В СЕТКЕ', el('input', {
            class: 'sfm-input', type: 'number', min: 1, max: 300, value: draft.maxCoins,
            oninput: (e) => { draft.maxCoins = Number(e.target.value) || 40; refresh(); },
          })),
          field('ТИП СОРТИРОВКИ', el('select', {
            class: 'sfm-select',
            onchange: (e) => { draft.sortType = e.target.value; refresh(); },
          }, SORT_TYPES.map((s) => el('option', {
            value: s.id, text: s.label, selected: s.id === draft.sortType,
          })))),
          field('ЧЁРНЫЙ СПИСОК', blacklistEditor(draft, refresh)),
        ])),
    ]),
    el('div', { class: 'sfm-right' }, [
      el('div', { class: 'sfm-preview-title', text: 'ЧТО ПОПАДЁТ В СЕТКУ' }),
      preview,
    ]),
  ]);

  const { close } = modal({
    title: isNew ? 'НОВЫЙ ФИЛЬТР' : 'РЕДАКТИРОВАТЬ ФИЛЬТР',
    body,
    width: 900,
    actions: [
      { label: 'Отмена', onClick: (c) => c() },
      {
        label: 'Сохранить', primary: true,
        onClick: (c) => {
          draft.name = draft.name.trim() || 'Без названия';
          onSave(draft);
          c();
        },
      },
    ],
  });

  renderPreview(preview, draft);
  return close;
}

// ------------------------------------------------------------- sub-blocuri

function section(title, hint, content) {
  return el('div', { class: 'sfm-section' }, [
    el('div', { class: 'sfm-section-head' }, [
      el('span', { class: 'sfm-section-title', text: title }),
      el('span', { class: 'sfm-section-hint', text: hint }),
    ]),
    content,
  ]);
}

function field(label, control) {
  return el('div', { class: 'sfm-field' }, [
    el('div', { class: 'sfm-field-label', text: label }),
    control,
  ]);
}

function conditionRow(cond, draft, refresh) {
  const enabledKey = `${cond.id}Enabled`;
  const inputs = el('div', { class: 'sfm-cond-inputs' });

  const numberInput = (key, placeholder) => el('input', {
    class: 'sfm-input sfm-input-sm',
    type: 'number',
    placeholder,
    value: draft[key] ?? '',
    oninput: (e) => {
      draft[key] = e.target.value === '' ? null : Number(e.target.value);
      refresh();
    },
  });

  inputs.append(
    numberInput(`${cond.id}Min`, 'от'),
    el('span', { class: 'sfm-cond-dash', text: '—' }),
    numberInput(`${cond.id}Max`, 'до'),
    cond.unit ? el('span', { class: 'sfm-cond-unit', text: cond.unit }) : null,
  );
  inputs.style.display = draft[enabledKey] ? 'flex' : 'none';

  const checkbox = el('input', {
    type: 'checkbox',
    checked: !!draft[enabledKey],
    onchange: (e) => {
      draft[enabledKey] = e.target.checked;
      inputs.style.display = e.target.checked ? 'flex' : 'none';
      refresh();
    },
  });

  return el('div', { class: 'sfm-cond' }, [
    el('label', { class: 'sfm-cond-head' }, [
      checkbox,
      el('span', { class: 'sfm-cond-label', text: cond.label }),
      el('span', { class: 'sfm-cond-help', title: cond.hint, text: '?' }),
    ]),
    inputs,
  ]);
}

function iconPicker(draft) {
  const grid = el('div', { class: 'sfm-icons' });
  for (const name of FILTER_ICONS) {
    grid.append(el('button', {
      class: `sfm-icon${name === draft.icon ? ' active' : ''}`,
      title: name,
      onclick: () => {
        draft.icon = name;
        [...grid.children].forEach((c) => c.classList.remove('active'));
        grid.querySelector(`[data-icon="${name}"]`)?.classList.add('active');
      },
      dataset: { icon: name },
    }, [icon(name, 15)]));
  }
  return grid;
}

function blacklistEditor(draft, refresh) {
  const list = el('div', { class: 'sfm-blacklist' });

  const render = () => {
    clear(list);
    for (const symbol of draft.blacklist) {
      list.append(el('span', { class: 'sfm-chip' }, [
        el('span', { text: baseTicker(symbol) }),
        el('button', {
          class: 'sfm-chip-x',
          onclick: () => {
            draft.blacklist = draft.blacklist.filter((s) => s !== symbol);
            render();
            refresh();
          },
        }, [icon('close', 10)]),
      ]));
    }
    list.append(el('input', {
      class: 'sfm-input sfm-input-sm',
      placeholder: 'Добавить',
      onkeydown: (e) => {
        if (e.key !== 'Enter') return;
        const symbol = e.target.value.trim().toUpperCase();
        if (!symbol) return;
        const full = market.get(symbol) ? symbol : `${symbol}USDT`;
        if (!draft.blacklist.includes(full)) draft.blacklist.push(full);
        e.target.value = '';
        render();
        refresh();
      },
    }));
  };

  render();
  return list;
}

function renderPreview(host, draft) {
  const rows = market.list();
  const { total, all } = apply(draft, rows);
  const limit = Number(draft.maxCoins) || 40;

  clear(host);
  host.append(
    el('div', { class: 'sfm-preview-count' }, [
      el('span', { class: 'sfm-preview-num', text: String(total) }),
      el('span', { class: 'sfm-preview-word', text: 'монет проходят фильтр' }),
    ]),
    el('div', {
      class: 'sfm-preview-note',
      text: `В сетке покажем первые ${Math.min(limit, total)} по выбранной сортировке`,
    }),
  );

  const list = el('div', { class: 'sfm-preview-list' });
  for (const row of all.slice(0, PREVIEW_ROWS)) {
    const change = Number(row.change24h) || 0;
    list.append(el('div', { class: 'sfm-preview-row' }, [
      el('span', { class: 'sfm-pr-ticker', text: baseTicker(row.symbol) }),
      el('span', { class: 'sfm-pr-vol', text: fmtVolume(row.quoteVolume) }),
      el('span', { class: `sfm-pr-change ${change >= 0 ? 'up' : 'down'}`, text: fmtPct(change, 1) }),
    ]));
  }
  if (total > PREVIEW_ROWS) {
    list.append(el('div', { class: 'sfm-preview-more', text: `и ещё ${total - PREVIEW_ROWS}` }));
  }
  if (!total) {
    list.append(el('div', { class: 'sfm-preview-empty', text: 'Ни одна монета не проходит фильтр' }));
  }
  host.append(list);
}
